import { PlusIcon, SearchIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@campai/lib-web/routes'
import {
    Button,
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@campai/lib-web/components/new/ui'

import { getTagFilterLabel, isTagFilterOption, TAG_FILTER_ALL, TAG_FILTER_NONE } from '@/product/feedback/tagFilter'
import { getStatusFilterLabel } from '@/product/feedback/status'
import {
    isStatusFilterOption,
    isTicketSortOption,
    statusFilterOptions,
    ticketSortOptions,
    type Search,
} from '@/product/feedback/home/routeUtils'

type ToolbarProps = {
    search: string
    setSearch: (search: string) => void
    submitSearch: () => void
    statusOption: Search['status']
    sortOption: Search['sort']
    tagFilter: string
    availableTags: string[]
    ticketCount: number | null
    canCreateTicket: boolean
    setTicketDialogOpen: (open: boolean) => void
    getListSearch: (overrides?: Partial<Search>) => Search
}

export const Toolbar = observer(
    ({
        search,
        setSearch,
        submitSearch,
        statusOption,
        sortOption,
        tagFilter,
        availableTags,
        ticketCount,
        canCreateTicket,
        setTicketDialogOpen,
        getListSearch,
    }: ToolbarProps) => {
        const { t } = useTranslation()
        const navigate = useNavigate()

        return (
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                    <InputGroup className="min-w-0 max-w-none w-full sm:w-72">
                        <InputGroupInput
                            value={search}
                            onChange={(event) => setSearch(event.currentTarget.value)}
                            onBlur={submitSearch}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.currentTarget.blur()
                                }
                            }}
                            placeholder={t('product.feedback.home.searchPlaceholder')}
                        />
                        <InputGroupAddon>
                            <SearchIcon className="size-4" />
                        </InputGroupAddon>
                    </InputGroup>
                    <Select
                        value={statusOption}
                        onValueChange={(value) => {
                            if (isStatusFilterOption(value)) {
                                void navigate({
                                    to: '/product/feedback',
                                    search: getListSearch({ page: 1, status: value }),
                                })
                            }
                        }}
                    >
                        <SelectTrigger
                            aria-label={t('product.feedback.home.filters.status')}
                            className="bg-background w-44"
                        >
                            <SelectValue>{getStatusFilterLabel(statusOption)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent align="start">
                            <SelectGroup>
                                {statusFilterOptions.map((status) => (
                                    <SelectItem key={status} value={status}>
                                        {getStatusFilterLabel(status)}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                    <Select
                        value={sortOption}
                        onValueChange={(value) => {
                            if (isTicketSortOption(value)) {
                                void navigate({
                                    to: '/product/feedback',
                                    search: getListSearch({ page: 1, sort: value }),
                                })
                            }
                        }}
                    >
                        <SelectTrigger
                            aria-label={t('product.feedback.home.filters.sort')}
                            className="bg-background w-44"
                        >
                            <SelectValue>{t(`product.feedback.home.sort.${sortOption}`)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent align="start">
                            <SelectGroup>
                                {ticketSortOptions.map((sort) => (
                                    <SelectItem key={sort} value={sort}>
                                        {t(`product.feedback.home.sort.${sort}`)}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                    <Select
                        value={tagFilter}
                        onValueChange={(value) => {
                            if (isTagFilterOption(value, availableTags)) {
                                void navigate({
                                    to: '/product/feedback',
                                    search: getListSearch({ page: 1, tag: value }),
                                })
                            }
                        }}
                    >
                        <SelectTrigger
                            aria-label={t('product.feedback.home.filters.tag')}
                            className="bg-background w-44"
                        >
                            <SelectValue>{getTagFilterLabel(tagFilter)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent align="start">
                            <SelectGroup>
                                <SelectItem value={TAG_FILTER_ALL}>
                                    {t('product.feedback.home.tagFilter.all')}
                                </SelectItem>
                                {availableTags.map((tag) => (
                                    <SelectItem key={tag} value={tag}>
                                        {tag}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                            <SelectSeparator />
                            <SelectGroup>
                                <SelectItem value={TAG_FILTER_NONE}>
                                    {t('product.feedback.home.tagFilter.none')}
                                </SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                    {ticketCount !== null && (
                        <span className="text-muted-foreground text-sm">
                            {t(
                                ticketCount === 1
                                    ? 'product.feedback.home.ticketCount_one'
                                    : 'product.feedback.home.ticketCount_other',
                                {
                                    count: ticketCount,
                                }
                            )}
                        </span>
                    )}
                </div>
                {canCreateTicket && (
                    <Button onClick={() => setTicketDialogOpen(true)}>
                        <PlusIcon data-icon="inline-start" />
                        {t('product.feedback.home.create')}
                    </Button>
                )}
            </div>
        )
    }
)
